// "Invitations for you" (spec A2, 2026-10-18): the pending invitations to the signed-in user's
// email, each with Accept, so an invitee who signs in without the email's link still joins. The
// click stays: without the link's token nothing shows they opened it (spec teams/invite-by-email).
// A user in no team sees them in place of the no-team screen; a user in a team, above each page.
import { Button, Group, Paper, Stack, Text } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { isApiError } from "../api/errors";
import {
  getListInvitationsForMeQueryKey,
  useAcceptInvitationForMe,
  useListInvitationsForMe,
} from "../api/generated/endpoints/teams/teams";
import type { InvitationForYou } from "../api/generated/model";
import { accessScopeQuery } from "../api/session";
import { ErrorAlert } from "./ErrorAlert";
import classes from "./InvitationsForYou.module.css";
import { ROLE_LABEL } from "./permissions";

/** "You've been invited to Acme as Editor by Ada.": "a team admin" when the inviter has no name. */
export function invitedLine(invitation: InvitationForYou): string {
  const role = ROLE_LABEL[invitation.role];
  const by = invitation.invited_by_name ?? "a team admin";
  return `You've been invited to ${invitation.team_name} as ${role} by ${by}.`;
}

/** The invitations, each with Accept; nothing while there are none. */
export function InvitationList({ invitations }: { invitations: readonly InvitationForYou[] }) {
  return (
    <ul className={classes.list} aria-label="Invitations for you">
      {invitations.map((invitation) => (
        <li key={invitation.id}>
          <InvitationRow invitation={invitation} />
        </li>
      ))}
    </ul>
  );
}

/** Above every page of a user in a team: their pending invitations, if any. */
export function InvitationsBanner() {
  const scope = useQuery(accessScopeQuery());
  const inTeam = (scope.data?.teams.length ?? 0) > 0;
  // A user in no team sees them on the no-team page instead (`NoTeamScreen`).
  const invitations = useListInvitationsForMe({ query: { enabled: inTeam } });
  if (!inTeam || invitations.data === undefined || invitations.data.length === 0) return null;
  return (
    <div className={classes.banner}>
      <InvitationList invitations={invitations.data} />
    </div>
  );
}

function InvitationRow({ invitation }: { invitation: InvitationForYou }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const accept = useAcceptInvitationForMe({
    mutation: {
      onSuccess: async ({ team_id }) => {
        // A new membership: the scope and everything cached under the old one are read again.
        queryClient.clear();
        await navigate({ to: "/teams/$teamId", params: { teamId: team_id } });
      },
      onError: async (error) => {
        // Revoked or expired since the list was read: read it again.
        if (isApiError(error, "NOT_FOUND")) {
          await queryClient.invalidateQueries({ queryKey: getListInvitationsForMeQueryKey() });
        }
      },
    },
  });
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="md">
          <Text size="sm">{invitedLine(invitation)}</Text>
          {/* It never shrinks: on a phone the line beside it wraps instead. */}
          <Button
            className={classes.accept}
            onClick={() => accept.mutate({ invitationId: invitation.id })}
            loading={accept.isPending}
            aria-label={`Accept the invitation to ${invitation.team_name}`}
          >
            Accept
          </Button>
        </Group>
        {accept.isError ? <AcceptFailed error={accept.error} /> : null}
      </Stack>
    </Paper>
  );
}

function AcceptFailed({ error }: { error: unknown }) {
  if (isApiError(error, "NOT_FOUND")) {
    return (
      <Text size="sm" role="alert">
        This invitation can no longer be used: it was revoked or has expired. Ask the team admin
        for a new one.
      </Text>
    );
  }
  if (isApiError(error, "ALREADY_MEMBER")) {
    return (
      <Text size="sm" role="alert">
        You are already a member of this team.
      </Text>
    );
  }
  return <ErrorAlert error={error} />;
}
